'use client';
import React, { useState, useMemo } from 'react';
import TripCard from '@/components/TripCard';
import { MOCK_TRIPS } from '@/lib/mockData';
import type { DifficultyLevel } from '@/lib/mockData';
import { Search, SlidersHorizontal, Grid3X3, List, X, ChevronDown } from 'lucide-react';

const CATEGORIES = ['All', 'Trek', 'Water Sports', 'Camping', 'Motorcycle', 'Safari', 'Adventure Sports'];
const DIFFICULTIES: DifficultyLevel[] = ['Easy', 'Moderate', 'Challenging', 'Extreme'];
const STATES = ['All', 'Uttarakhand', 'Himachal Pradesh', 'Karnataka', 'Rajasthan', 'Andaman & Nicobar', 'Nagaland', 'Meghalaya'];
const DURATIONS = ['Any', '1–3 days', '4–6 days', '7+ days'];
const SORT_OPTIONS = [
  { value: 'default', label: 'Featured First' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'duration-asc', label: 'Duration: Short First' },
  { value: 'seats', label: 'Seats Available' },
];

export default function TripsListingContent() {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDifficulties, setSelectedDifficulties] = useState<DifficultyLevel[]>([]);
  const [selectedState, setSelectedState] = useState('All');
  const [selectedDuration, setSelectedDuration] = useState('Any');
  const [maxPrice, setMaxPrice] = useState(30000);
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 9;

  const toggleDifficulty = (d: DifficultyLevel) => {
    setSelectedDifficulties((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    );
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('All');
    setSelectedDifficulties([]);
    setSelectedState('All');
    setSelectedDuration('Any');
    setMaxPrice(30000);
    setSortBy('default');
    setPage(1);
  };

  const filtered = useMemo(() => {
    let trips = MOCK_TRIPS.filter((t) => t.status !== 'Draft' && t.status !== 'Archived');

    if (search.trim()) {
      const q = search.toLowerCase();
      trips = trips.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.location.toLowerCase().includes(q) ||
          t.state.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q)
      );
    }
    if (selectedCategory !== 'All') trips = trips.filter((t) => t.category === selectedCategory);
    if (selectedDifficulties.length > 0) trips = trips.filter((t) => selectedDifficulties.includes(t.difficulty));
    if (selectedState !== 'All') trips = trips.filter((t) => t.state === selectedState);
    if (selectedDuration !== 'Any') {
      if (selectedDuration === '1–3 days') trips = trips.filter((t) => t.duration <= 3);
      else if (selectedDuration === '4–6 days') trips = trips.filter((t) => t.duration >= 4 && t.duration <= 6);
      else if (selectedDuration === '7+ days') trips = trips.filter((t) => t.duration >= 7);
    }
    trips = trips.filter((t) => t.price <= maxPrice);

    switch (sortBy) {
      case 'price-asc': trips = [...trips].sort((a, b) => a.price - b.price); break;
      case 'price-desc': trips = [...trips].sort((a, b) => b.price - a.price); break;
      case 'duration-asc': trips = [...trips].sort((a, b) => a.duration - b.duration); break;
      case 'seats': trips = [...trips].sort((a, b) => (b.totalSeats - b.bookedSeats) - (a.totalSeats - a.bookedSeats)); break;
      default: trips = [...trips].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }

    return trips;
  }, [search, selectedCategory, selectedDifficulties, selectedState, selectedDuration, maxPrice, sortBy]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const activeFilterCount = [
    selectedCategory !== 'All',
    selectedDifficulties.length > 0,
    selectedState !== 'All',
    selectedDuration !== 'Any',
    maxPrice < 30000,
  ].filter(Boolean).length;

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
      {/* Top Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search trips, destinations..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input-field pl-10"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Sort */}
        <div className="relative">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="input-field pr-8 appearance-none min-w-[180px] cursor-pointer"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={`sort-${opt.value}`} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border font-semibold text-sm transition-all ${
            showFilters || activeFilterCount > 0
              ? 'bg-[#E8440A] border-[#E8440A] text-white'
              : 'bg-white border-gray-200 text-gray-700 hover:border-[#E8440A] hover:text-[#E8440A]'
          }`}
        >
          <SlidersHorizontal size={15} />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-white/25 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>
          )}
        </button>

        {/* View toggle */}
        <div className="flex border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-3 py-2.5 transition-colors ${viewMode === 'grid' ? 'bg-[#1A3C5E] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
          >
            <Grid3X3 size={16} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-2.5 transition-colors ${viewMode === 'list' ? 'bg-[#1A3C5E] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-6 shadow-sm animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {/* Category */}
            <div>
              <p className="label-field">Category</p>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((cat) => (
                  <button
                    key={`cat-${cat}`}
                    onClick={() => { setSelectedCategory(cat); setPage(1); }}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
                      selectedCategory === cat
                        ? 'bg-[#1A3C5E] border-[#1A3C5E] text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-[#1A3C5E]'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Difficulty */}
            <div>
              <p className="label-field">Difficulty</p>
              <div className="flex flex-wrap gap-1.5">
                {DIFFICULTIES.map((d) => (
                  <button
                    key={`diff-${d}`}
                    onClick={() => toggleDifficulty(d)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
                      selectedDifficulties.includes(d)
                        ? 'bg-[#E8440A] border-[#E8440A] text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-[#E8440A]'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {/* State */}
            <div>
              <p className="label-field">State / Region</p>
              <div className="relative">
                <select
                  value={selectedState}
                  onChange={(e) => { setSelectedState(e.target.value); setPage(1); }}
                  className="input-field appearance-none pr-8 cursor-pointer"
                >
                  {STATES.map((s) => (
                    <option key={`state-${s}`} value={s}>{s}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Duration */}
            <div>
              <p className="label-field">Duration</p>
              <div className="flex flex-wrap gap-1.5">
                {DURATIONS.map((d) => (
                  <button
                    key={`dur-${d}`}
                    onClick={() => { setSelectedDuration(d); setPage(1); }}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
                      selectedDuration === d
                        ? 'bg-[#1A3C5E] border-[#1A3C5E] text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-[#1A3C5E]'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div>
              <p className="label-field">Max Price: <span className="text-[#E8440A] font-mono">₹{maxPrice.toLocaleString('en-IN')}</span></p>
              <input
                type="range"
                min={1000}
                max={30000}
                step={500}
                value={maxPrice}
                onChange={(e) => { setMaxPrice(Number(e.target.value)); setPage(1); }}
                className="w-full accent-[#E8440A]"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>₹1,000</span>
                <span>₹30,000</span>
              </div>
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button onClick={clearFilters} className="flex items-center gap-1.5 text-sm text-[#E8440A] font-semibold hover:underline">
                <X size={14} /> Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Results Info */}
      <div className="flex items-center justify-between mb-5">
        <p className="text-gray-600 text-sm">
          Showing <span className="font-bold text-[#1A3C5E]">{filtered.length}</span> trip{filtered.length !== 1 ? 's' : ''}
          {search && <span className="text-gray-400"> for "{search}"</span>}
        </p>
        {filtered.length > 0 && totalPages > 1 && (
          <p className="text-gray-400 text-sm">Page {page} of {totalPages}</p>
        )}
      </div>

      {/* Trip Grid / List */}
      {paginated.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Search size={28} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-700 mb-2">No trips found</h3>
          <p className="text-gray-500 text-sm mb-6">Try adjusting your filters or search query to find available trips.</p>
          <button onClick={clearFilters} className="btn-primary">Clear Filters</button>
        </div>
      ) : (
        <div className={
          viewMode === 'grid' ?'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6' :'flex flex-col gap-4'
        }>
          {paginated.map((trip) => (
            <TripCard key={trip.id} trip={trip} viewMode={viewMode} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-10">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:border-[#E8440A] hover:text-[#E8440A] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-xl text-sm font-bold transition-all ${
                page === i + 1
                  ? 'bg-[#E8440A] text-white'
                  : 'border border-gray-200 text-gray-600 hover:border-[#E8440A] hover:text-[#E8440A]'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:border-[#E8440A] hover:text-[#E8440A] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}